import React, { useEffect, useRef, useState } from "react";
import UploadFile from "../upload-file/upload-file";
import { NewMobileAppProps } from "./mobile-app-add-new.props";
import { NewMobileAppViewModel } from "./mobile-app-add-new.view-model";
import { useAuth } from "../auth/auth-context";
import ErrorPopup from "../error-popup/error-popup";
import {
	APNSCertificateType,
	IFileInfo,
	IMobileAppModel,
} from "src/types/models/apiModels";
import CheckBox from "src/components/molecules/check-box/check-box";
import RadioGroup from "src/components/molecules/radio-button-group/radio-button-groups";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import ActionPanel from "../action-panel/action-panel";
import BoxWithIcon from "src/components/molecules/box-with-icon/box-with-icon";
import { ReactComponent as AppleIcon } from "../../../assets/svg/apple.svg";
import { ReactComponent as AndroidIcon } from "../../../assets/svg/android.svg";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import { MapPinIcon } from "@heroicons/react/24/solid";
const NewMobileApp: React.FC<NewMobileAppProps> = ({
	mobileApp,
	onCancel,
	onSave,
}) => {
	const [fileContent, setFileContent] = useState(null);
	const { user } = useAuth(); // Extract user from useAuth hook
	type CertificateFileFormat = "P8" | "P12";
	const [showAPN, setShowAPN] = useState(mobileApp?.iOSAppSettings != null);
	const [showFCM, setShowFCM] = useState(
		mobileApp?.androidAppSettings != null
	);
	const [iosCertFileFormat, setIOSCertFileFormat] =
		useState<CertificateFileFormat>(
			mobileApp?.iOSAppSettings?.certificateType ?? "P12"
		);
	const [iosCertFile, setIOSCertFile] = useState<IFileInfo | undefined>(
		mobileApp?.iOSAppSettings?.certificateFile
	);
	const [androidCertFile, setAndroidCertFile] = useState<
		IFileInfo | undefined
	>(mobileApp?.androidAppSettings?.certificateFile);
	const [name, setName] = useState<string>("");
	const [bundleId, setBundleId] = useState<string>("");
	const [disableBundleId, setDisableBundleId] = useState<boolean>(
		mobileApp?.iOSAppSettings?.bundleId != null
	);
	const [applePlatformKeyID, setApplePlatformKeyID] = useState<string>("");
	const [applePlatformTeamID, setApplePlatformTeamID] = useState<string>("");
	const [p12Password, setP12Password] = useState<string | undefined>(
		mobileApp?.iOSAppSettings?.p12FileSettings?.p12Password
	);
	const [packageName, setPackageName] = useState<string>(
		mobileApp?.androidAppSettings?.packageName ?? ""
	);
	const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [mode, setMode] = useState("New");
	const [requestUserLocation, setRequestUserLocation] = useState(false);
	const viewModel = useRef<NewMobileAppViewModel | null>(null);

	useEffect(() => {
		if (!mobileApp) return;

		setMode("Edit");
		setName(mobileApp.name);

		const { iOSAppSettings, androidAppSettings } = mobileApp;

		if (iOSAppSettings) {
			setShowAPN(true);
			setBundleId(iOSAppSettings.bundleId);

			const { p12FileSettings, p8FileSettings } = iOSAppSettings;

			if (p12FileSettings) {
				setIOSCertFileFormat(APNSCertificateType.P12);
				setP12Password(p12FileSettings.p12Password);
			} else if (p8FileSettings) {
				setIOSCertFileFormat(APNSCertificateType.P8);
				setApplePlatformKeyID(p8FileSettings.applePlatformKeyID);
				setApplePlatformTeamID(p8FileSettings.applePlatformTeamID);
			}
		}

		if (androidAppSettings) {
			setShowFCM(true);
			setPackageName(androidAppSettings.packageName);
		}
	}, [mobileApp]);

	useEffect(() => {
		if (user?.accessToken) {
			viewModel.current = new NewMobileAppViewModel(user.accessToken);
		}
		handleRadioChange({ label: "P12", value: "P12" });
	}, [user]);

	const onIOSCertUploadSuccess = (fileInfo: IFileInfo | undefined) => {
		if (fileInfo) {
			setIOSCertFile(fileInfo);
		}
	};

	const onAndroidCertUploadSuccess = (fileInfo: IFileInfo | undefined) => {
		if (fileInfo) {
			setAndroidCertFile(fileInfo);
		}
	};

	const onIOSCertClear = () => {
		setIOSCertFile(undefined);
	};

	const onAndroidCertClear = () => {
		setAndroidCertFile(undefined);
	};

	const toggleError = (value: boolean) => {
		console.log("In toggleError");
		setErrorDialogOpen((prevValue) => {
			if (prevValue !== value) {
				return value;
			}
			return prevValue;
		});
	};

	const closeError = () => {
		console.log("In closeError");
		toggleError(false);
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel();
		}
	};

	const handleBackToList = () => {
		if (onCancel) {
			onCancel();
		}
	};

	const handleRadioChange = (selectedOption: {
		label: string;
		value: string;
	}) => {
		if (selectedOption.value === "P8" || selectedOption.value === "P12") {
			setIOSCertFileFormat(selectedOption.value as CertificateFileFormat);
		} else {
			console.error("Invalid certificate file format selected.");
		}
	};

	const handleSaveOrUpdateAsync = async (
		updatedMobileApp: IMobileAppModel
	) => {
		console.log("handleSaveOrUpdate", updatedMobileApp);
		const response =
			mode === "Edit"
				? await viewModel.current?.updateMobileAppAsync({
						brandingAppCode: updatedMobileApp.id,
						code: updatedMobileApp.code,
						name: updatedMobileApp.name,
						iOSApp: {
							action:
								!showAPN && mobileApp?.iOSAppSettings
									? "Remove"
									: mobileApp &&
										  mobileApp.iOSAppSettings &&
										  mobileApp.iOSAppSettings
												.certificateFile &&
										  mobileApp.iOSAppSettings
												.certificateFile.signedUrl
										? "Update"
										: "Add",
							iOSAppSettings: showAPN
								? updatedMobileApp.iOSAppSettings
								: undefined,
						},
						androidApp: {
							action:
								!showFCM && mobileApp?.androidAppSettings
									? "Remove"
									: mobileApp &&
										  mobileApp.androidAppSettings &&
										  mobileApp.androidAppSettings
												.certificateFile &&
										  mobileApp.androidAppSettings
												.certificateFile.signedUrl
										? "Update"
										: "Add",
							androidAppSettings: showFCM
								? updatedMobileApp.androidAppSettings
								: undefined,
						},
					})
				: await viewModel.current?.saveMobileAppAsync(updatedMobileApp);

		if (response?.result === true) {
			if (onSave) {
				onSave(updatedMobileApp);
			}
		} else {
			console.log(`Error: ${JSON.stringify(response)}`);
			console.log(`Error: ${response?.error}`);
			setError(() => response?.error ?? "");
			toggleError(true);
		}
	};

	async function handleSubmit(): Promise<void> {
		if (!name) {
			return;
		}

		console.log("handleSubmit mobileApp", mobileApp);

		// if (showFCM) {
		// 	const p12File = iosCertFile?.signedUrl;

		// 	if (p12File) {
		// 		const response = await fetch(p12File);
		// 		if (!response.ok) {
		// 			throw new Error("Failed to download the file from S3");
		// 		}

		// 		const arrayBuffer = await response.arrayBuffer();
		// 		const p12Asn1 = forge.asn1.fromDer(
		// 			forge.util.createBuffer(arrayBuffer)
		// 		);
		// 		const p12 = forge.pkcs12.pkcs12FromAsn1(p12Asn1, p12Password);

		// 		// Extract certificate and keys
		// 		const certBags = p12.getBags({
		// 			bagType: forge.pki.oids.certBag,
		// 		});

		// 		if (certBags) {
		// 			const certs = certBags[forge.pki.oids.certBag]?.map(
		// 				(bag) => {
		// 					if (bag && bag.cert) {
		// 						forge.pki.certificateToPem(bag.cert);
		// 					}
		// 				}
		// 			);

		// 			const keyBags = p12.getBags({
		// 				bagType: forge.pki.oids.pkcs8ShroudedKeyBag,
		// 			});
		// 			if (keyBags) {
		// 				const keys = keyBags[
		// 					forge.pki.oids.pkcs8ShroudedKeyBag
		// 				]?.map((bag) => {
		// 					if (bag.key) forge.pki.privateKeyToPem(bag.key);
		// 				});
		// 				setError("");
		// 			}
		// 		}
		// 	}
		// }

		const mobileRequest: IMobileAppModel = {
			id: mobileApp?.id ?? "",
			code: mobileApp?.code ?? "",
			name: name,
			iOSAppSettings:
				bundleId && iosCertFile
					? {
							bundleId: bundleId,
							certificateType:
								iosCertFileFormat === "P8"
									? APNSCertificateType.P8
									: APNSCertificateType.P12,
							certificateFile: iosCertFile,
							p8FileSettings:
								iosCertFileFormat === "P8"
									? {
											applePlatformTeamID:
												applePlatformTeamID ?? "",
											applePlatformKeyID:
												applePlatformKeyID ?? "",
											applePlatformBundleID:
												bundleId ?? "",
										}
									: undefined,
							p12FileSettings:
								iosCertFileFormat === "P12"
									? { p12Password: p12Password ?? "" }
									: undefined,
						}
					: undefined,
			androidAppSettings:
				packageName && androidCertFile
					? {
							packageName: packageName,
							certificateFile: androidCertFile,
						}
					: undefined,
			bundleId: bundleId,
			packageName: packageName,
		};

		if (mobileRequest) {
			await handleSaveOrUpdateAsync(mobileRequest);
		}
	}

	return (
		<>
			{/* Help Text */}
			{/* <HelpText
				help={[
					"You have the option to register an iOS app, an Android app, or both.",
					"To proceed, select the appropriate checkboxes below. Based on your selection, you will be prompted to provide the necessary details for Apple Push Notification (APN) or Firebase Cloud Messaging (FCM). This will ensure that your applications are properly configured for branding the calls",
				]}
				alignment="center"
			/> */}

			{/* Checkboxes to control section visibility */}
			<div className="bg-white p-6 flex flex-col items-center justify-start gap-12 w-full">
				{fileContent && (
					<div className="mt-4 p-4 border rounded-md bg-gray-100">
						<h4 className="font-medium mb-2">File Content:</h4>
						<pre className="text-sm text-gray-800">
							{JSON.stringify(fileContent, null, 2)}
						</pre>
					</div>
				)}
				<InputBox
					label="Mobile App Name"
					value={name}
					type={"text"}
					onChange={function (value: string): void {
						setName(value);
					}}
					disabled={mode === "Edit"}
					required
				></InputBox>

				<div className="flex items-start justify-start w-full h-full gap-6">
					{/* Apple Push Notification (APN) */}
					<BoxWithIcon
						icon={
							<div className="flex items-center justify-center gap-2 bg-white rounded-full p-1 m-1">
								<AppleIcon className="w-4 h-4"></AppleIcon>
							</div>
						}
						title={"Apple push notification (APN)"}
					>
						<div className="flex flex-col items-start justify-start gap-4 w-full p-4">
							<div className="flex items-center justify-start gap-12 w-full">
								<CheckBox
									title={"Enabled"}
									onChange={function (value: boolean): void {
										setShowAPN(value);
									}}
									checked={showAPN}
								></CheckBox>
							</div>
							{showAPN && (
								<>
									<div className="flex flex-col items-start justify-center gap-4 w-full">
										<span className="font-semibold text-md">
											Choose Authentication Format
										</span>

										<RadioGroup
											options={[
												{
													label: "P8",
													value: "P8",
													disabled: true,
												},
												{
													label: "P12",
													value: "P12",
												},
											]}
											defaultSelected="P12"
											onChange={handleRadioChange}
										/>
										<div className="flex flex-col items-start justify-start gap-6 w-full h-full">
											<div
												className={`flex flex-col items-start justify-start gap-4 border-2 border-dashed border-gray-300 p-4 rounded-lg w-full ${
													mobileApp
														?.androidAppSettings
														?.certificateFile
														.fileName != ""
														? ""
														: "border-red-500 border-b-4 pb-1"
												} `}
											>
												<div className="flex items-center justify-center gap-4">
													<span className="font-Poppins font-semibold text-md">
														Existing certificate
														file :
													</span>
													{mobileApp &&
													mobileApp.iOSAppSettings &&
													mobileApp.iOSAppSettings
														.certificateFile &&
													mobileApp.iOSAppSettings
														.certificateFile
														.fileName != "" ? (
														<span className="font-Poppins font-semibold text-md">
															{
																mobileApp
																	?.iOSAppSettings
																	?.certificateFile
																	.fileName
															}
														</span>
													) : (
														<span className="font-Poppins font-semibold text-md">
															-
														</span>
													)}
												</div>
												<UploadFile
													label="Upload certificate"
													onUploadSuccess={
														onIOSCertUploadSuccess
													}
													onClear={onAndroidCertClear}
												/>
											</div>

											<InputBox
												label={`Bundle ID`}
												value={`${bundleId}`}
												type={"text"}
												onChange={function (
													value: string
												): void {
													setBundleId(value);
												}}
												disabled={disableBundleId}
												required
											></InputBox>

											{iosCertFileFormat === "P8" && (
												<>
													<InputBox
														label="Key ID"
														type={"text"}
														value={
															applePlatformKeyID
														}
														onChange={function (
															value: string
														): void {
															setApplePlatformKeyID(
																value
															);
														}}
														required
													></InputBox>
													<InputBox
														label="Team ID"
														type={"text"}
														value={
															applePlatformTeamID
														}
														onChange={function (
															value: string
														): void {
															setApplePlatformTeamID(
																value
															);
														}}
														required
													></InputBox>
												</>
											)}
											{iosCertFileFormat === "P12" && (
												<>
													<InputBox
														label={"Password"}
														type={"password"}
														value={p12Password}
														onChange={function (
															value: string
														): void {
															setP12Password(
																value
															);
														}}
													></InputBox>
												</>
											)}
											<div>
												<ButtonPro
													label={"Test coming soon"}
													disabled
												></ButtonPro>
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</BoxWithIcon>

					{/* Firebase Cloud Messaging (FCM) */}
					<BoxWithIcon
						icon={
							<div className="flex items-center justify-center gap-2 bg-white rounded-full p-1 m-1">
								<AndroidIcon className="w-4 h-4"></AndroidIcon>
							</div>
						}
						title={"Firebase cloud messaging (FCM)"}
					>
						<div className="flex flex-col items-start justify-start gap-6 w-full p-4">
							<CheckBox
								title={"Enabled"}
								onChange={function (value: boolean): void {
									setShowFCM(value);
								}}
								checked={showFCM}
							></CheckBox>
							{showFCM && (
								<>
									<div className="flex flex-col items-start justify-start gap-4 w-full h-full">
										<div className="flex flex-col items-start justify-start w-full gap-6">
											<div className="border-2 border-dashed border-gray-300 p-4 rounded-lg w-full">
												<div
													className={`flex flex-col items-start justify-start gap-2 ${
														mobileApp
															?.androidAppSettings
															?.certificateFile
															.fileName == ""
															? "border-red-500 border-b-4 pb-1"
															: ""
													} `}
												>
													<div className="flex items-center justify-center gap-4">
														<span className="font-Poppins font-semibold text-md">
															Existing service
															account key file :
														</span>
														{mobileApp &&
														mobileApp.androidAppSettings &&
														mobileApp
															.androidAppSettings
															.certificateFile &&
														mobileApp
															.androidAppSettings
															.certificateFile
															.fileName != "" ? (
															<span className="font-Poppins font-semibold text-md">
																{
																	mobileApp
																		?.androidAppSettings
																		?.certificateFile
																		.fileName
																}
															</span>
														) : (
															<span className="font-Poppins font-semibold text-md">
																-
															</span>
														)}
													</div>
													<UploadFile
														label="Upload your firebase service account key"
														onUploadSuccess={
															onAndroidCertUploadSuccess
														}
														onClear={
															onAndroidCertClear
														}
													/>
												</div>
											</div>
											<InputBox
												label="Package name"
												value={packageName}
												type={"text"}
												onChange={function (
													value: string
												): void {
													setPackageName(value);
												}}
												disabled={
													packageName !== "" &&
													mode === "Edit"
												}
												required
											></InputBox>
											<div>
												<ButtonPro
													label={"Test coming soon"}
													disabled
												></ButtonPro>
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</BoxWithIcon>
				</div>
				<BoxWithIcon
					icon={
						<div className="flex items-center justify-center gap-2 rounded-full p-1 m-1">
							<MapPinIcon className="w-4 h-4"></MapPinIcon>
						</div>
					}
					title={"Location services"}
				>
					<div className="w-full flex items-center justify-start p-4">
						<CheckBox
							title="Request user location"
							checked={requestUserLocation}
							onChange={function (value: boolean): void {
								setRequestUserLocation(value);
							}}
						></CheckBox>
					</div>
				</BoxWithIcon>

				<ErrorPopup
					message={error}
					open={errorDialogOpen}
					onClose={closeError}
				></ErrorPopup>

				<ActionPanel
					primaryButtonLabel={"Submit"}
					primaryButtonEnabled={
						name !== "" &&
						(showAPN || showFCM) &&
						(showAPN
							? iosCertFile !== undefined && bundleId !== ""
							: true) &&
						(showFCM
							? androidCertFile !== undefined &&
								packageName !== ""
							: true)
					}
					secondaryButtonLabel={"Cancel"}
					secondaryButtonEnabled={true}
					primaryButtonAction={handleSubmit}
					secondaryButtonAction={handleBackToList}
				></ActionPanel>
			</div>
		</>
	);
};

export default NewMobileApp;
